import React, { useEffect, useState, useMemo, useCallback } from "react";
import Layout, { Container } from "../components/layout";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { apiurl } from "../config/config";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowLeft, EditIcon } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { getCookie } from "../config/webStorage";
import { useUser } from "../config/userProvider";
import { PackagePlus, Calendar, IndianRupee, Hash } from "lucide-react";
import { ArrowUpRight, Factory, Users } from "lucide-react";
import StatusBadge from "../ui/StatusBadge";
import { Boxes, ArrowDown, ArrowUp } from "lucide-react";
import { TrendingDown, RefreshCcw } from "lucide-react";
import { SuwasthiLoader } from "../components/loader";

const MaterialDetails = () => {
  const { materialId } = useParams();
  const [material, setMaterial] = useState(null);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stockInOpen, setStockInOpen] = useState(false);
  const [stockOutOpen, setStockOutOpen] = useState(false);

  let token = getCookie("SWFMSToken");
  const { userData } = useUser();
  console.log("User Data:", userData);

  const navigate = useNavigate();
  // const qtyUnit = material?.unit ?? "";

  // const fetchMaterial = async (materialId) => {
  //   setLoading(true);
  //   try {
  //     const resp = await axios.get(
  //       `${apiurl}/api/material/report/${materialId}`,
  //       {
  //         headers: {
  //           Authorization: `${token}`,
  //         },
  //       }
  //     );
  //     setMaterial(resp.data.material || {});
  //     setSummary(resp.data.summary || {});
  //     setHistory(resp.data.movementHistory || []);
  //   } catch (err) {
  //     console.error(err);
  //     setError("Failed to load material details.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  // useEffect(() => {
  //   fetchMaterial(materialId);
  // }, [materialId]);

    // ✅ Wrap fetchMaterial in useCallback
  const fetchMaterial = useCallback(
    async (id = materialId) => {
      setLoading(true);
      try {
        const resp = await axios.get(`${apiurl}/api/material/report/${id}`, {
          headers: { Authorization: token },
        });

        setMaterial(resp.data.material || {});
        setSummary(resp.data.summary || {});
        setHistory(resp.data.movementHistory || []);
        setError(null);
      } catch (err) {
        console.error(err);
        setError("Failed to load material details.");
      } finally {
        setLoading(false);
      }
    },
    [materialId, token] // ✅ include dependencies
  );

  // ✅ Now useEffect can safely include fetchMaterial
  useEffect(() => {
    fetchMaterial();
  }, [fetchMaterial]);


  const stockCards = [
    {
      label: "Total In",
      value: summary?.totalIn,
      icon: ArrowDown,
      color: "bg-green-600",
    },
    {
      label: "Total Out",
      value: summary?.totalOut,
      icon: ArrowUp,
      color: "bg-red-600",
    },
    {
      label: "Min Stock Level",
      value: material?.minStockLevel,
      icon: TrendingDown,
      color: "bg-yellow-500",
    },
    {
      label: "Reorder Qty",
      value: material?.reorderQty || 0,
      icon: RefreshCcw,
      color: "bg-blue-600",
    },
  ];

  const stockPercent = Math.min(
    (material?.quantity / material?.minStockLevel) * 100,
    100
  );
  const isLowStock = material?.quantity <= material?.minStockLevel;



  const [open, setOpen] = useState(false);
  const authToken = getCookie("SWFMSToken");

  if (loading) return <SuwasthiLoader />;

  return (
    <Layout>
      <Container
        overflow={"overflow-scroll"}
        className="h-full flex flex-col p-4 space-y-6"
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-gray-200 transition"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <h1 className="text-3xl font-bold text-gray-800">
              Material Details
            </h1>
            <EditIcon
              onClick={() => setOpen(true)}
              className="w-5 h-5 text-gray-700 cursor-pointer hover:text-indigo-600"
            />

            {/* Modal */}
            {open && (
              <EditMaterialModal
                open={open}
                setOpen={setOpen}
                material={material}
                authToken={authToken}
                apiBase={apiurl}
                onUpdated={(m) => {
                  console.log("Updated Material:", m);
                }}
              />
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setStockInOpen(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700"
            >
              Stock In
            </button>
            <button
              onClick={() => setStockOutOpen(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg shadow hover:bg-red-700"
            >
              Stock Out
            </button>
          </div>
        </div>

        <div className="relative bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl p-6 text-white shadow-xl">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold capitalize">{material?.name}</h1>
              <p className="text-sm opacity-90">
                Material ID: {material?.materialId}
              </p>
            </div>

            <Boxes className="w-12 h-12 opacity-80" />
          </div>

          <div className="mt-6">
            <p className="text-sm uppercase tracking-wide opacity-90">
              Current Stock
            </p>
            <p className="text-6xl font-extrabold leading-none">
              {material?.quantity|| 0}
              <span className="text-2xl ml-2 font-medium">
                {material?.unit}
              </span>
            </p>
          </div>

          {isLowStock && (
            <div className="mt-4 inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-semibold">
              <AlertTriangle size={16} />
              Low Stock (Min: {material?.minStockLevel} {material?.unit})
            </div>
          )}
        </div>

        <div className="mt-8 bg-white rounded-2xl shadow p-6 border border-gray-200">
          <h2 className="text-xl font-bold mb-4">📦 Material Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <Detail label="Type" value={material?.materialType} />
            <Detail label="Category" value={material?.category} />
            <Detail label="Sub Category" value={material?.subCategory} />

            <Detail label="Unit" value={material?.unit} />
            <Detail
              label="Price / Unit"
              value={`₹ ${material?.pricePerUnit}`}
            />
            <Detail label="Stock Status" value={material?.stockStatus} />

            <Detail
              label="GST"
              value={`${material?.cgst}% + ${material?.sgst}%`}
            />
            <Detail label="Active" value={material?.isActive ? "Yes" : "No"} />
            <Detail
              label="Approved"
              value={material?.isApproved ? "Yes" : "No"}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          {stockCards.map((c) => (
            <div
              key={c.label}
              className={`${c.color} text-white rounded-xl p-4 shadow-lg flex justify-between items-center`}
            >
              <div>
                <p className="text-sm opacity-90">{c.label}</p>
                <p className="text-2xl font-bold">
                  {c.value} {material?.unit}
                </p>
              </div>
              <c.icon size={34} className="opacity-80" />
            </div>
          ))}
        </div>

        <div className="mt-6 bg-white rounded-2xl p-6 shadow">
          <p className="font-semibold mb-2">Stock Level Indicator</p>

          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className={`h-4 rounded-full ${
                isLowStock ? "bg-red-500" : "bg-green-500"
              }`}
              style={{ width: `${stockPercent}%` }}
            />
          </div>

          <p className="text-sm mt-2 text-gray-600">
            {material?.quantity} / {material?.minStockLevel} {material?.unit}
          </p>
        </div>

        <div className="mt-6 bg-white rounded-2xl shadow p-6">
          <h2 className="text-xl font-bold mb-4">📄 Material Ledger</h2>

          <MaterialLedgerTable history={history} qtyUnit={material?.unit} />
        </div>

        {loading && (
          <motion.p
            className="text-center py-10 text-gray-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            Loading material details...
          </motion.p>
        )}
        {error && (
          <motion.p
            className="text-center py-10 text-red-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {error}
          </motion.p>
        )}

        <StockInModal
          isOpen={stockInOpen}
          onClose={(refresh) => {
            setStockInOpen(false);
            if (refresh) fetchMaterial();
          }}
          material={material}
          token={token} // Pass token to StockInModal
        />
        <StockOutPopup
          isOpen={stockOutOpen}
          onClose={(refresh) => {
            setStockOutOpen(false);
            if (refresh) fetchMaterial();
          }}
          materialId={materialId}
          material={material}
          token={token} // Pass token to StockOutPopup
        />
      </Container>
    </Layout>
  );
};

export default MaterialDetails;


const StockInModal = ({ isOpen, onClose, material, token }) => {
  console.log("token in StockInModal:", token);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [form, setForm] = useState({
    poNumber: "",
    quantity: "",
    unit: material?.unit || "",
    batchNumber: "",
    receivedDate: new Date().toISOString().split("T")[0],
    supplierName: "",
    pricePerUnit: "",
    remarks: "",
  });

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);

  console.log("form in StockInModal:", form);
  // ✅ Fetch suppliers list when modal opens
  useEffect(() => {
    if (isOpen) {
      axios
        .get(`${apiurl}/api/supplier/get`)
        .then((res) => {
          const list = res.data.data || [];
          setSuppliers(list);
          if (list.length === 1) {
            setForm((prev) => ({ ...prev, supplierName: list[0].name }));
          }
        })
        .catch((err) => console.error("Supplier fetch error:", err));
    }
  }, [isOpen]);

  const totalValue =
    Number(form.quantity || 0) * Number(form.pricePerUnit || 0);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(
        `${apiurl}/api/material/stock-in`,
        {
          materialId: material.materialId,
          materialIdObjId: material._id,
          quantity: Number(form.quantity),
          unit: form.unit,
          batchNumber: form.batchNumber,
          receivedDate: form.receivedDate,
          supplierName: form.supplierName,
          supplierCode: form.supplierCode,
          pricePerUnit: Number(form.pricePerUnit),
          remarks: form.remarks,
        },
        {
          headers: {
            Authorization: token, // ✅ no Bearer prefix in your curl example
            "Content-Type": "application/json",
          },
        }
      );

      onClose(true); // refresh parent data
    } catch (err) {
      console.error("Stock In failed:", err.response?.data || err.message);
      alert("Failed to add stock.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <SuwasthiLoader />;
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl p-6"
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-green-100 text-green-700">
                <PackagePlus />
              </div>
              <div>
                <h2 className="text-xl font-bold">Stock In</h2>
                <p className="text-sm text-gray-500">
                  {material?.name} ({material?.materialId})
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
              {/* PO Number */}
              <div>
                <label className="text-sm font-medium">PO Number</label>
                <div className="relative">
                  <Hash className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                  <input
                    name="poNumber"
                    required
                    onChange={handleChange}
                    className="pl-10 w-full border rounded-xl p-2 uppercase"
                  />
                </div>
              </div>

              {/* Supplier */}
              {/* <div>
                <label className="text-sm font-medium">Supplier</label>
                <input
                  name="supplierName"
                  required
                  onChange={handleChange}
                  className="w-full border rounded-xl p-2"
                />
              </div> */}

              {/* Supplier */}
              <div>
                <label className="block text-sm font-medium text-gray-600">
                  Supplier
                </label>
                {/* <select
                  name="supplierName"
                  value={form.supplierName}
                  onChange={handleChange}
                  required
                  className="mt-1 w-full border rounded-md p-2"
                >
                  <option value="">-- Select Supplier --</option>
                  {suppliers.map((s) => (
                    <option key={s._id} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select> */}
                <select
                  name="supplier"
                  value={selectedSupplier?._id || ""}
                  onChange={(e) => {
                    const supplier = suppliers.find(
                      (s) => s._id === e.target.value
                    );
                    setSelectedSupplier(supplier || null);
                    setForm((prev) => ({
                      ...prev,
                      supplierName: supplier?.name || "",
                      supplierCode: supplier?.supplierCode || "", // ✅ also save code
                    }));
                  }}
                  required
                  className="mt-1 w-full border rounded-xl p-2"
                >
                  <option value="">-- Select Supplier --</option>
                  {suppliers.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name} ({s.code})
                    </option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="text-sm font-medium">Received Date</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    name="receivedDate"
                    value={form.receivedDate}
                    required
                    onChange={handleChange}
                    className="pl-10 w-full border rounded-xl p-2"
                  />
                </div>
              </div>

              {/* Quantity */}
              <div>
                <label className="text-sm font-medium">
                  Quantity ({material?.unit})
                </label>
                <input
                  type="number"
                  name="quantity"
                  required
                  onChange={handleChange}
                  className="w-full border rounded-xl p-2"
                />
              </div>

              {/* Price */}
              <div>
                <label className="text-sm font-medium">Price / Unit</label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                  <input
                    type="number"
                    name="pricePerUnit"
                    required
                    onChange={handleChange}
                    className="pl-10 w-full border rounded-xl p-2"
                  />
                </div>
              </div>

              {/* Total */}
              <div>
                <label className="text-sm font-medium">Total Value</label>
                <div className="bg-gray-100 rounded-xl p-2 font-semibold">
                  ₹ {totalValue.toFixed(2)}
                </div>
              </div>

              {/* Remarks */}
              <div className="col-span-2">
                <label className="text-sm font-medium">Remarks</label>
                <textarea
                  name="remarks"
                  rows={3}
                  onChange={handleChange}
                  className="w-full border rounded-xl p-2"
                />
              </div>

              {/* Actions */}
              <div className="col-span-2 flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => onClose(false)}
                  className="px-4 py-2 rounded-xl border"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 rounded-xl bg-green-600 text-white shadow hover:bg-green-700"
                >
                  Save Stock
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
const StockOutPopup = ({ isOpen, onClose, material, token }) => {
  const [type, setType] = useState("internal");
  const today = new Date().toISOString().split("T")[0];
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    quantityIssued: "",
    unit: "",
    issuedTo: "",
    issueDate: new Date().toISOString().split("T")[0],
    remarks: "",
  });

  // const isInvalid =
  //   Object.keys(errors).length > 0 ||
  //   !formData.quantityIssued ||
  //   !formData.issueDate;

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newErrors = { ...errors };

    if (name === "quantityIssued") {
      const qty = Number(value);

      if (!qty || qty <= 0) {
        newErrors.quantityIssued = "Quantity must be greater than 0";
      } else if (qty > material.quantity) {
        newErrors.quantityIssued = `Available stock is ${material.quantity} ${material.unit}`;
      } else {
        delete newErrors.quantityIssued;
      }
    }

    if (name === "issueDate") {
      if (!value) {
        newErrors.issueDate = "Issue date is required";
      } else if (value > today) {
        newErrors.issueDate = "Future date not allowed";
      } else {
        delete newErrors.issueDate;
      }
    }

    if (name === "issuedTo") {
      if (!value) newErrors.issuedTo = "This field is required";
      else delete newErrors.issuedTo;
    }

    setErrors(newErrors);
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${apiurl}/api/material/stock-issue`,
        {
          materialId: material.materialId,
          materialIdObjId: material._id,
          ...formData,
        },
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      setFormData({
        quantityIssued: "",
        unit: "",
        issuedTo: "",
        issueDate: "",
        remarks: "",
      });

      onClose(true);
    } catch (error) {
      console.error(error);
      alert("Error issuing stock");
    }
  };

  return (
    isOpen && (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
        <div className="bg-white w-full max-w-xl rounded-2xl shadow-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-red-100 text-red-700">
              <ArrowUpRight />
            </div>
            <h2 className="text-xl font-bold">Stock Out</h2>
          </div>

          {/* Available Stock */}
          <div className="bg-indigo-50 rounded-xl p-3 text-sm mb-4">
            Available Stock:{" "}
            <b>
              {material?.quantity} {material?.unit}
            </b>
          </div>

          {/* Type */}
          <div className="flex gap-4 mb-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={type === "internal"}
                onChange={() => setType("internal")}
              />
              <Factory size={16} /> Internal
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={type === "external"}
                onChange={() => setType("external")}
              />
              <Users size={16} /> External
            </label>
          </div>

          <form className="space-y-3">
            <div>
              <label className="text-sm font-medium">Quantity</label>
              <input
                name="quantityIssued"
                type="number"
                value={formData.quantityIssued}
                onChange={handleChange}
                className={`w-full border rounded-xl p-2 mt-1 ${
                  errors.quantityIssued ? "border-red-500" : ""
                }`}
                placeholder="Enter quantity"
              />
              <p className="text-xs text-gray-500 mt-1">
                Available: {material.quantity} {material.unit}
              </p>
              {errors.quantityIssued && (
                <p className="text-xs text-red-600 mt-1">
                  {errors.quantityIssued}
                </p>
              )}
            </div>

            <div>
              {type === "internal" ? (
                <select
                  name="issuedTo"
                  value={formData.issuedTo}
                  onChange={handleChange}
                  className={`w-full border rounded-xl p-2 ${
                    errors.issuedTo ? "border-red-500" : ""
                  }`}
                >
                  <option value="">Select Department</option>
                  <option>Production</option>
                  <option>Extraction</option>
                  <option>Packaging</option>
                </select>
              ) : (
                <input
                  name="issuedTo"
                  value={formData.issuedTo}
                  onChange={handleChange}
                  placeholder="Buyer / Party Name"
                  className={`w-full border rounded-xl p-2 ${
                    errors.issuedTo ? "border-red-500" : ""
                  }`}
                />
              )}

              {errors.issuedTo && (
                <p className="text-xs text-red-600 mt-1">{errors.issuedTo}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium">Issue Date</label>
              <input
                name="issueDate"
                type="date"
                value={formData.issueDate}
                max={today}
                onChange={handleChange}
                className={`w-full border rounded-xl p-2 mt-1 ${
                  errors.issueDate ? "border-red-500" : ""
                }`}
              />
              {errors.issueDate && (
                <p className="text-xs text-red-600 mt-1">{errors.issueDate}</p>
              )}
            </div>

            <textarea
              rows={3}
              placeholder="Remarks"
              className="w-full border rounded-xl p-2"
            />

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => onClose(false)}
                type="button"
                className="px-4 py-2 border rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-5 py-2 bg-red-600 text-white rounded-xl shadow hover:bg-red-700"
              >
                Issue Stock
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );
};

const EditMaterialModal = ({
  material,
  authToken,
  apiBase,
  open,
  setOpen,
  onUpdated,
}) => {
  const [form, setForm] = useState({
    name: material?.name || "",
    materialType: material?.materialType || "raw_material",
    category: material?.category || "",
    quantity: Number.isFinite(material?.quantity) ? material.quantity : 0,
    unit: material?.unit || "kg",
    minStockLevel: Number.isFinite(material?.minStockLevel) ? material.minStockLevel : 0,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const materialTypeOptions = [
    { value: "raw_material", label: "Raw Material" },
    { value: "packaging_material", label: "Packaging Material" },
    // { value: "finished_goods", label: "Finished Goods" },
    // { value: "miscellaneous", label: "Miscellaneous" },
  ];
  const unitTypeOptions = [
    { value: "kg", label: "Kilograms" },
    { value: "ltr", label: "Liters" },
    { value: "pcs", label: "Pieces" },
    { value: "mtr", label: "Meters" },
  ];

  const changedFields = useMemo(() => {
  const changes = {};

  if (form.name !== (material?.name || ""))
    changes.name = form.name;

  if (form.materialType !== (material?.materialType || "raw_material"))
    changes.materialType = form.materialType;

  if (form.category !== (material?.category || ""))
    changes.category = form.category;

  if (form.unit !== (material?.unit || "kg"))
    changes.unit = form.unit;

  if (Number(form.quantity) !== Number(material?.quantity || 0))
    changes.quantity = Number(form.quantity);

  if (Number(form.minStockLevel) !== Number(material?.minStockLevel || 0))
    changes.minStockLevel = Number(form.minStockLevel);

  return changes;
}, [form, material]);

  

  const disabled = useMemo(() => {
    if (!form.name?.trim()) return true;
    if (!form.category?.trim()) return true;
    if (!form.materialType) return true;
    if (!Number.isFinite(Number(form.quantity)) || Number(form.quantity) < 0)
      return true;
    // Disable if nothing actually changed
    return Object.keys(changedFields).length === 0;
  }, [form, changedFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "quantity") {
      const v = value.replace(/[^0-9.]/g, "");
      setForm((p) => ({ ...p, [name]: v }));
    } else {
      setForm((p) => ({ ...p, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSubmitting(true);
    try {
      const payload = {
        materialIdObjId: material?._id,
        ...changedFields,
      };
      const res = await axios.post(`${apiBase}/api/material/update`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: authToken,
        },
      });
      setSuccess("Material updated successfully.");
      window.location.reload(); // Reload to reflect changes
      if (typeof onUpdated === "function") {
        onUpdated(res?.data?.material || res?.data);
      }
      setOpen(false);
    } catch (err) {
      const msg =
        err?.response?.data?.message || err.message || "Update failed";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          />

          {/* Card */}
          <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl border border-gray-100">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Edit Material</h2>
              <button
                className="rounded-full p-2 hover:bg-gray-100"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Enter material name"
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Material Type */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Material Type
                </label>
                <select
                  name="materialType"
                  value={form.materialType}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {materialTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Category
                </label>
                <input
                  type="text"
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  placeholder="e.g. Metals"
                  className="w-full lowercase rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium mb-1">Unit</label>
                <select
                  name="unit"
                  value={form.unit}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {unitTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  min={0}
                  step="1"
                  name="quantity"
                  value={form.quantity}
                  onChange={handleChange}
                  placeholder="0"
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Minimum Stock Level
                </label>
                <input
                  type="number"
                  min={0}
                  step="1"
                  name="minStockLevel"
                  value={form.minStockLevel}
                  onChange={handleChange}
                  placeholder="0"
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Error / Success */}
              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}
              {success && (
                <div className="rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                  {success}
                </div>
              )}

              {/* Actions */}
              <div className="mt-6 flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="rounded-xl px-4 py-2 text-sm border border-gray-200 hover:bg-gray-50"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || disabled}
                  className={`rounded-xl px-4 py-2 text-sm font-medium text-white shadow-sm transition ${
                    submitting || disabled
                      ? "bg-indigo-300 cursor-not-allowed"
                      : "bg-indigo-600 hover:bg-indigo-700"
                  }`}
                >
                  {submitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>

            {/* Meta */}
            <div className="mt-4 text-[11px] text-gray-500">
              Updating fields: name, materialType, category, quantity. ID:{" "}
              {material?._id}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function MaterialLedgerTable({ history, qtyUnit }) {
  const getLedgerDate = (row) => {
    if (row.type === "IN") {
      return row.receivedDate || row.receivedBy?.receiveDate || row.createdAt;
    }
    if (row.type === "OUT") {
      return row.issueDate || row.issuedBy?.issueDate || row.createdAt;
    }
    return row.createdAt;
  };

  return (
    <div className="overflow-x-auto rounded-xl border">
      <table className="min-w-full text-sm">
        <thead className="bg-indigo-50">
          <tr>
            {[
              "#",
              "Type",
              "Qty",
              "Cost",
              "From / To",
              "Handled By",
              "Receive / Issue Date",
              "Remarks",
            ].map((h) => (
              <th key={h} className="px-4 py-3 text-left font-semibold">
                {h}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="divide-y">
          {history.length === 0 && (
            <tr>
              <td colSpan="7" className="text-center py-6 text-gray-500">
                No ledger entries found
              </td>
            </tr>
          )}

          {history.map((row, i) => (
            <tr key={row._id} className="hover:bg-gray-50">
              <td className="px-4 py-2">{i + 1}</td>

              <td className="px-4 py-2">
                <StatusBadge status={row.type} />
              </td>

              <td className="px-4 py-2 font-medium">
                {row.quantity} {qtyUnit}
              </td>

              <td className="px-4 py-2">₹ {row.totalCost || "-"}</td>

              <td className="px-4 py-2">
                {row.issuedTo || row.supplierName || "-"}
              </td>
              <td className="px-4 py-2">
                {row.type === "IN"
                  ? row.receivedBy?.userName || "-"
                  : row.issuedBy?.userName || "-"}
              </td>

              <td className="px-4 py-2 text-gray-500">
                {/* {new Date(row.createdAt).toLocaleString()} */}
                {new Date(getLedgerDate(row)).toLocaleString()}
              </td>

              <td className="px-4 py-2 text-gray-600">{row.remarks || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div>
    <p className="text-gray-500">{label}</p>
    <p className="font-semibold text-gray-800">{value || "-"}</p>
  </div>
);